import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Badge,
  Box,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Column,
  IconButton,
  Row,
  SearchInput,
  Text,
  Tooltip,
  TextWithTooltip,
} from "@hightouch/htui";
import orderBy from "lodash/orderBy";
import { isPresent } from "ts-extras";

import searchPlaceholder from "src/assets/placeholders/search.svg";
import type {
  DataPoint,
  GraphSeries,
  GroupColumn,
} from "src/components/charts/engine/types";
import { getSeriesDescription } from "src/components/charts/engine/utils";
import {
  getColumnNameFromGroupByColumn,
  getModelIdFromColumn,
  getPropertyNameFromColumn,
} from "src/components/explore/visual/utils";
import { useAnalyticsContext } from "src/pages/analytics/state";
import { ChartVariant } from "src/pages/analytics/types";
import {
  getNumberOfUniqueValues,
  isGroupByColumnRelatedToParent,
  shouldUsePercentFormat,
} from "src/pages/analytics/utils";
import { Table, type TableColumn } from "src/ui/table";
import { accurateCommaNumber } from "src/utils/numbers";

import { STATIC_ARRAY } from "src/pages/analytics/state/constants";
import { AnalyticsBar } from "./analytics-bar";
import {
  audienceNameKey,
  type BreakdownColumn,
  getColumnKeys,
  getGroupingColumnPath,
  getGroupingKey,
  getMaxBreakdownValue,
  formatBreakdownValue,
  getMaxDecimalPlaces,
  getTdStyles,
  type GroupByColumnPath,
  metricNameKey,
  move,
  type MoveableBreakdownColumn,
  splitNameKey,
  valueKey,
  groupKey,
  type BreakdownColumnKind,
  useColumnSizer,
} from "./utils";
import type { GroupByColumn } from "@hightouch/lib/query/visual/types/analytics";
import {
  BarChartLoader,
  ChartWithLoaderText,
} from "src/components/charts/loaders";

export type BreakdownTableProps = {
  data: GraphSeries[];
  showHeaders?: boolean;
  groupByColumns?: GroupByColumn[];
  isLoading?: boolean;
  enableSearch?: boolean;
  // Optionally specify the columns and their order to display (excluding value column which is always last)
  columnsOrder?: MoveableBreakdownColumn[];
  // The table's value column will not have a header unless a valueColumnName is provided
  valueColumnName?: string;
  // The number of rows to show per view
  rowsPerView?: number;
  // The maximum number of rows for the table
  maxRowsCount?: number;
  // Show error bars on the bar if this is enabled and the DataPoint has errorBounds data
  showErrorBars?: boolean;
  // Sort the table by columns
  autoSort?: boolean;
  // Custom tooltip for the bar column
  customDataTooltip?: (dataPoint: DataPoint) => ReactNode;
  // Whole-number percentage by which to scale down the bar width if needed
  barScalePercentage?: number;
  variant?: ChartVariant;
  // Props from <ResponsiveContainer>
  width?: number;
  height?: number;
};

export const BreakdownTable: FC<BreakdownTableProps> = ({
  data,
  groupByColumns = STATIC_ARRAY,
  isLoading = false,
  showHeaders = true,
  enableSearch = true,
  columnsOrder,
  valueColumnName,
  rowsPerView = 50,
  maxRowsCount,
  showErrorBars = false,
  autoSort = true,
  customDataTooltip,
  barScalePercentage,
  variant = ChartVariant.Full,
  width = undefined,
}) => {
  const hasSplits = data.some(({ splitName }) => Boolean(splitName));
  const isFull = variant == ChartVariant.Full;
  const isEmbedded = variant == ChartVariant.Embedded;
  const numberOfUniqueAudiences = getNumberOfUniqueValues(data, "audienceName");

  const { parent } = useAnalyticsContext();
  const [visibleCount, setVisibleCount] = useState(rowsPerView);

  const [search, setSearch] = useState("");

  const groupByColumnPaths: GroupByColumnPath[] = useMemo(
    () =>
      (data?.[0]?.grouping || groupByColumns)
        .map((_, groupingIndex) => getGroupingColumnPath(groupingIndex))
        .filter(isPresent),
    [data, groupByColumns],
  );

  const [columnKeys, setColumnKeys] = useState<MoveableBreakdownColumn[]>(
    getColumnKeys(
      groupByColumnPaths,
      hasSplits,
      numberOfUniqueAudiences,
      columnsOrder,
    ),
  );

  useEffect(() => {
    setColumnKeys(
      getColumnKeys(
        groupByColumnPaths,
        hasSplits,
        numberOfUniqueAudiences,
        columnsOrder,
      ),
    );
  }, [groupByColumnPaths, hasSplits, numberOfUniqueAudiences, columnsOrder]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const filteredData = useMemo(() => {
    const result = data.filter(
      ({ metricName, splitName, description, grouping, data }) => {
        const groupMetadata =
          grouping?.flatMap(({ column, value }) => [
            getPropertyNameFromColumn(column)?.toLowerCase() ?? "",
            value?.toLowerCase() ?? "--",
          ]) ?? [];
        const lowerCasedSearch = search.toLowerCase().trim();

        const formattedMetricValue =
          data?.[0]?.metricValue !== undefined
            ? accurateCommaNumber(data?.[0]?.metricValue)
            : undefined;

        return (
          metricName.toLowerCase().trim().includes(lowerCasedSearch) ||
          description.toLowerCase().trim().includes(lowerCasedSearch) ||
          splitName?.toLowerCase().includes(lowerCasedSearch) ||
          groupMetadata.filter((value) => value.includes(lowerCasedSearch))
            .length > 0 ||
          (formattedMetricValue &&
            (formattedMetricValue.includes(lowerCasedSearch) ||
              formattedMetricValue.replace(",", "").includes(lowerCasedSearch)))
        );
      },
    );

    if (!autoSort) {
      return result;
    }

    const sortKeys = (columnKeys as string[])
      .slice(0, columnKeys.length - 1)
      .concat(["data.0.metricValue"]);

    const directionArray = Array(sortKeys.length - 1)
      .fill("asc")
      // Last column is a number (value) so it'll be sorted in descending order
      .concat("desc");

    return orderBy(result, sortKeys, directionArray);
  }, [columnKeys, data, search]);

  const pageData = filteredData.slice(0, visibleCount);

  // Apply maxRowsCount limit if provided
  const maxRows = maxRowsCount
    ? Math.min(filteredData.length, maxRowsCount)
    : filteredData.length;

  const hasMoreVisible = visibleCount < maxRows;
  const reachedMaxRowsCount =
    maxRowsCount && filteredData.length > maxRowsCount && !hasMoreVisible;

  const showMoreRows = () => {
    setVisibleCount(Math.min(visibleCount + rowsPerView, maxRows));
  };

  const maxValue = useMemo(
    () => getMaxBreakdownValue(data, showErrorBars),
    [data, showErrorBars],
  );
  const maxDecimalPlaces = useMemo(() => getMaxDecimalPlaces(data), [data]);
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const groupings = useMemo(
    () =>
      filteredData.flatMap(({ grouping }) =>
        grouping?.map(({ column, value }) => ({
          [getGroupingKey(column, parent)]: value,
        })),
      ),
    [filteredData],
  );

  const getColumnMinMax = useColumnSizer(variant, width);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const columnsByKey = useMemo(() => {
    const map: Partial<
      Record<
        BreakdownColumn | typeof valueKey,
        { kind: BreakdownColumnKind } & TableColumn
      >
    > = {
      [metricNameKey]: {
        kind: metricNameKey,
        headerSx: { border: "none", pl: "0 !important" },
        header: () => (
          <Row align="center" gap={2}>
            <Text
              color="text.secondary"
              variant="monospace"
              fontWeight="medium"
            >
              Metric{" "}
            </Text>
            <Box>
              <Badge size="sm">
                {getNumberOfUniqueValues(data, metricNameKey)}
              </Badge>
            </Box>
            {columnKeys.length > 1 && (
              <Box>
                <Tooltip message="Move column to the left">
                  <IconButton
                    aria-label="Move metric name column to the left."
                    icon={ChevronLeftIcon}
                    isDisabled={columnKeys[0] === metricNameKey}
                    size="sm"
                    onClick={() =>
                      setColumnKeys(move(columnKeys, metricNameKey, "left"))
                    }
                  />
                </Tooltip>
                <Tooltip message="Move column to the right">
                  <IconButton
                    aria-label="Move metric name column to the right."
                    icon={ChevronRightIcon}
                    isDisabled={
                      columnKeys[columnKeys.length - 1] === metricNameKey
                    }
                    size="sm"
                    onClick={() =>
                      setColumnKeys(move(columnKeys, metricNameKey, "right"))
                    }
                  />
                </Tooltip>
              </Box>
            )}
          </Row>
        ),
        cellSx: {
          display: "contents",
        },
        cell: ({ metricName }, index) => {
          const isFirstRow = index === 0;
          const isFirstOfGroup =
            pageData?.[index - 1]?.metricName !== metricName;
          const isLastOfGroup =
            pageData?.[index + 1]?.metricName !== metricName;
          const isLastColumn =
            columnKeys.indexOf("metricName") === columnKeys.length - 1;

          return (
            <Box
              sx={getTdStyles({
                isFirstRow,
                isFirstOfGroup,
                isLastColumn,
                isLastOfGroup,
                variant,
              })}
            >
              <TextWithTooltip
                message={metricName}
                size="sm"
                variant="monospace"
                fontWeight="normal"
              >
                {metricName}
              </TextWithTooltip>
            </Box>
          );
        },
      },
      [splitNameKey]: {
        kind: splitNameKey,
        headerSx: { border: "none", pl: "0 !important" },
        header: () => (
          <Row align="center" gap={2}>
            <Text
              color="text.secondary"
              variant="monospace"
              fontWeight="medium"
            >
              Split
            </Text>
            <Box>
              <Badge size="sm">
                {getNumberOfUniqueValues(data, splitNameKey, [undefined])}
              </Badge>
            </Box>
            <Box>
              <Tooltip message="Move column to the left">
                <IconButton
                  aria-label="Move split name column to the left."
                  icon={ChevronLeftIcon}
                  isDisabled={columnKeys[0] === splitNameKey}
                  size="sm"
                  onClick={() =>
                    setColumnKeys(move(columnKeys, splitNameKey, "left"))
                  }
                />
              </Tooltip>
              <Tooltip message="Move column to the right">
                <IconButton
                  aria-label="Move split name column to the right."
                  icon={ChevronRightIcon}
                  isDisabled={
                    columnKeys[columnKeys.length - 1] === splitNameKey
                  }
                  size="sm"
                  onClick={() =>
                    setColumnKeys(move(columnKeys, splitNameKey, "right"))
                  }
                />
              </Tooltip>
            </Box>
          </Row>
        ),
        cellSx: {
          display: "contents",
        },
        cell: ({ splitName }: GraphSeries, index) => {
          const isFirstRow = index === 0;
          const isFirstOfGroup = pageData?.[index - 1]?.splitName !== splitName;
          const isLastOfGroup = pageData?.[index + 1]?.splitName !== splitName;
          const isLastColumn =
            columnKeys.indexOf("splitName") === columnKeys.length - 1;

          return (
            <Box
              sx={getTdStyles({
                isFirstRow,
                isFirstOfGroup,
                isLastColumn,
                isLastOfGroup,
                variant,
              })}
            >
              <TextWithTooltip
                color="text.secondary"
                message={splitName ?? "--"}
                size="sm"
                variant="monospace"
              >
                {splitName ?? "--"}
              </TextWithTooltip>
            </Box>
          );
        },
      },
      [valueKey]: {
        kind: valueKey,
        cellSx: {
          border: "none",
          display: isEmbedded ? "contents" : undefined,
        },
        headerSx: { border: "none" },
        header: valueColumnName
          ? () => (
              <Row align="center">
                <Text
                  color="text.secondary"
                  variant="monospace"
                  size="sm"
                  fontWeight="medium"
                >
                  {valueColumnName}
                </Text>
              </Row>
            )
          : undefined,
        cell: ({
          color = "indigo.500",
          audienceName,
          splitName,
          grouping,
          metricName,
          aggregation,
          data,
          normalization,
        }: GraphSeries) => {
          const metricValue = data?.[0]?.metricValue;
          const errorBounds = data?.[0]?.errorBounds;
          const errorBar =
            showErrorBars && errorBounds
              ? {
                  ...errorBounds,
                  value: metricValue ?? 0,
                }
              : undefined;

          if (maxValue === undefined || metricValue === undefined) {
            return <Text color="text.tertiary">Not available</Text>;
          }

          const value = shouldUsePercentFormat(aggregation, normalization)
            ? `${(metricValue * 100).toFixed(2)}%`
            : formatBreakdownValue(metricValue, maxDecimalPlaces);

          const fillWidth = 100 * (metricValue / maxValue);
          const fullName = getSeriesDescription({
            audienceName:
              numberOfUniqueAudiences > 1 ? audienceName : undefined,
            splitName,
            groupByColumns: grouping,
          });

          return (
            <Column justifyContent="center" height="100%">
              <AnalyticsBar
                metricName={metricName}
                value={value}
                color={color}
                width={fillWidth}
                barScalePercentage={barScalePercentage}
                errorBar={errorBar}
                tooltip={
                  customDataTooltip && data[0]
                    ? {
                        type: "custom",
                        customTooltip: customDataTooltip(data[0]),
                      }
                    : {
                        type: "default",
                        defaultTooltipProps: {
                          tooltipValue: value,
                          tooltipText: fullName,
                        },
                      }
                }
              />
            </Column>
          );
        },
      },
    };

    if (numberOfUniqueAudiences > 1) {
      map[audienceNameKey] = {
        kind: audienceNameKey,
        headerSx: { border: "none", pl: "0 !important" },
        disabled: () => true,
        header: () => (
          <Row align="center" gap={2}>
            <Text
              color="text.secondary"
              variant="monospace"
              fontWeight="medium"
            >
              Audience
            </Text>
            <Box>
              <Badge size="sm">{numberOfUniqueAudiences}</Badge>
            </Box>
            <Box>
              <Tooltip message="Move column to the left">
                <IconButton
                  aria-label="Move audience name column to the left."
                  icon={ChevronLeftIcon}
                  isDisabled={columnKeys[0] === audienceNameKey}
                  size="sm"
                  onClick={() =>
                    setColumnKeys(move(columnKeys, audienceNameKey, "left"))
                  }
                />
              </Tooltip>
              <Tooltip message="Move column to the right">
                <IconButton
                  aria-label="Move audience name column to the right."
                  icon={ChevronRightIcon}
                  isDisabled={
                    columnKeys[columnKeys.length - 1] === audienceNameKey
                  }
                  size="sm"
                  onClick={() =>
                    setColumnKeys(move(columnKeys, audienceNameKey, "right"))
                  }
                />
              </Tooltip>
            </Box>
          </Row>
        ),
        cellSx: {
          display: "contents",
        },
        cell: ({ audienceName }, index) => {
          const isFirstRow = index === 0;
          const isFirstOfGroup =
            pageData?.[index - 1]?.audienceName !== audienceName;
          const isLastOfGroup =
            pageData?.[index + 1]?.audienceName !== audienceName;
          const isLastColumn =
            columnKeys.indexOf("audienceName") === columnKeys.length - 1;

          return (
            <Box
              sx={getTdStyles({
                isFirstRow,
                isFirstOfGroup,
                isLastColumn,
                isLastOfGroup,
                variant,
              })}
            >
              <TextWithTooltip message={audienceName} size="sm">
                {audienceName}
              </TextWithTooltip>
            </Box>
          );
        },
      };
    }

    // Add grouping columns
    (data?.[0]?.grouping || groupByColumns).forEach(
      (column: GroupByColumn | GroupColumn, groupingIndex) => {
        const columnName = getColumnNameFromGroupByColumn(column);

        const groupByColumn = (
          "column" in column ? column.column : column
        ) as GroupByColumn;
        const modelId = getModelIdFromColumn(groupByColumn);
        const groupingPath = getGroupingColumnPath(groupingIndex);

        if (columnName) {
          map[groupingPath] = {
            kind: groupKey,
            headerSx: { border: "none", pl: "0 !important" },
            header: () => {
              return (
                <Row align="center" gap={2}>
                  <Text
                    color="text.secondary"
                    variant="monospace"
                    fontWeight="medium"
                  >
                    {columnName}
                  </Text>
                  <Box>
                    <Badge size="sm">
                      {getNumberOfUniqueValues(
                        groupings,
                        getGroupingKey(groupByColumn, parent),
                        [undefined],
                      )}
                    </Badge>
                  </Box>
                  {columnKeys.length > 1 && (
                    <Box>
                      <Tooltip message="Move column to the left">
                        <IconButton
                          aria-label="Move audience name column to the left."
                          icon={ChevronLeftIcon}
                          isDisabled={columnKeys[0] === groupingPath}
                          size="sm"
                          onClick={() =>
                            setColumnKeys(
                              move(columnKeys, groupingPath, "left"),
                            )
                          }
                        />
                      </Tooltip>
                      <Tooltip message="Move column to the right">
                        <IconButton
                          aria-label="Move audience name column to the right."
                          icon={ChevronRightIcon}
                          isDisabled={
                            columnKeys[columnKeys.length - 1] === groupingPath
                          }
                          size="sm"
                          onClick={() =>
                            setColumnKeys(
                              move(columnKeys, groupingPath, "right"),
                            )
                          }
                        />
                      </Tooltip>
                    </Box>
                  )}
                </Row>
              );
            },
            cellSx: {
              display: "contents",
            },
            cell: ({ grouping }: GraphSeries, index) => {
              const value = grouping?.find((groupingColumn) => {
                const groupingColumnName =
                  getColumnNameFromGroupByColumn(groupingColumn);
                const groupingColumnModelId = getModelIdFromColumn(
                  groupingColumn.column,
                );

                // We want to group event columns of the same name but we don't
                // want to group it with the parent model columns so make sure
                // to check the groupByColumn modelId when the groupBy is related
                // the parent model
                if (isGroupByColumnRelatedToParent(parent, groupByColumn)) {
                  return (
                    groupingColumnName === columnName &&
                    groupingColumnModelId === modelId
                  );
                }

                return (
                  groupingColumnName === columnName &&
                  groupingColumnModelId !== parent?.id?.toString()
                );
              });

              if (!value) {
                return (
                  <Text color="text.secondary" size="sm">
                    --
                  </Text>
                );
              }

              const previousRowValue = pageData?.[index - 1]?.grouping?.find(
                ({ column: groupingColumn }) =>
                  getPropertyNameFromColumn(groupingColumn) === columnName,
              );
              const nextRowValue = pageData?.[index + 1]?.grouping?.find(
                ({ column: groupingColumn }) =>
                  getPropertyNameFromColumn(groupingColumn) === columnName,
              );
              const isFirstRow = index === 0;
              const isFirstOfGroup = previousRowValue?.value !== value.value;
              const isLastOfGroup = nextRowValue?.value !== value.value;
              const isLastColumn =
                columnKeys.indexOf(groupingPath) === columnKeys.length - 1;

              return (
                <Box
                  sx={getTdStyles({
                    isFirstRow,
                    isFirstOfGroup,
                    isLastColumn,
                    isLastOfGroup,
                    variant,
                  })}
                >
                  <TextWithTooltip
                    color="text.primary"
                    size="sm"
                    message={value.value ?? "--"}
                  >
                    {value.value ?? "--"}
                  </TextWithTooltip>
                </Box>
              );
            },
          };
        }
      },
    );

    return map;
  }, [data, groupByColumns, groupings, pageData, variant, isEmbedded]);

  const tableColumns = useMemo(() => {
    // Always append the value column at the end
    const columns = [
      ...columnKeys.map(
        (key) => columnsByKey[key as keyof typeof columnsByKey],
      ),
      columnsByKey[valueKey],
    ].filter(isPresent);

    return columns.map(({ kind, ...column }, index) => ({
      ...column,
      ...getColumnMinMax({ kind, isLastColumn: index == columns.length - 2 }),
    }));
  }, [columnKeys, columnsByKey, getColumnMinMax]);

  return (
    <Column flex={1} minHeight={0} gap={4}>
      {!isLoading && enableSearch && data.length > 0 && (
        <SearchInput
          placeholder="Search series…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      {isLoading ? (
        <ChartWithLoaderText gap={7}>
          <BarChartLoader />
        </ChartWithLoaderText>
      ) : (
        <Row
          flex={1}
          height="100%"
          {...(isFull
            ? {
                overflow: "auto",
                mb: 4,
              }
            : {
                overflowX: "auto",
                overflowY: "scroll",
                // For backwards scrolling
                flexDir: "row-reverse",
                sx: { containerType: "inline-size" },
              })}
        >
          <Column gap={2} flex={1}>
            <Table
              showHeaders={showHeaders}
              defaultMin="min-content"
              columns={tableColumns}
              data={pageData}
              loading={isLoading}
              rowHeight="32px"
              placeholder={{
                image: searchPlaceholder,
                title: "No breakdowns found",
                error:
                  "Breakdowns failed to load. Please check your configuration and try again.",
              }}
            />
            {hasMoreVisible && filteredData.length > 0 && (
              // Revealing already-loaded rows must preserve the current route and query params.
              <Box px={4} py={2}>
                <Button type="button" variant="link" onClick={showMoreRows}>
                  Show more rows
                </Button>
              </Box>
            )}

            {reachedMaxRowsCount && (
              <Alert
                mt={2}
                type="subtle"
                variant="inline"
                title={`Only showing top ${maxRowsCount} rows`}
              />
            )}
          </Column>
        </Row>
      )}
    </Column>
  );
};
